// ─── Vocabulaires du JOURNAL DE BORD ──────────────────────────────
// Une note déclare sa NATURE, et c'est la nature qui désigne le vocabulaire
// proposé. MoPCal aplatissait les deux niveaux dans un seul champ `categorie`
// à 5 valeurs : « santé » et « école » y étaient des VALEURS de catégorie,
// tandis que « humeur » et « incident » étaient des NOMS de vocabulaires —
// « sport » et « humeur » ne sont pas au même niveau. Découpage décidé par
// Cédric le 09/08 ; la liste des catégories d'événement reste INCHANGÉE.
//
// Nature « note » → catégories d'ÉVÉNEMENT réutilisées telles quelles : une
// seule liste globale, partagée avec le calendrier, configurable un jour dans
// la configuration du calendrier (pas ici).
// Extension `.js` EXPLICITE (le reste du dépôt l'omet) : ce module est chargé
// tel quel par `node --test` via journal.test.mjs, et l'ESM natif de Node
// n'ajoute pas l'extension — Metro, lui, accepte les deux. Ne pas « nettoyer ».
import { CATEGORIES_EVT, emojiCategorie, labelCategorie } from './categories.js';

// Id sentinelle de la puce « Autre… » : ne se stocke JAMAIS en base, il ne
// sert qu'à basculer la saisie libre dans le formulaire.
export const VALEUR_LIBRE = '__libre';

// `libre: true` → la valeur peut être SAISIE au lieu d'être choisie. Elle vaut
// alors pour CETTE note seulement : rien n'est ajouté au vocabulaire, aucune
// liste n'est modifiée. Lister tous les incidents possibles est impossible —
// d'où la saisie libre sur cette nature-là, et sur elle seule.
export const NATURES_NOTE = [
  { id: 'note',     emoji: '📝', i18n: 'journal.natNote',      label: 'Note',     libre: false },
  { id: 'humeur',   emoji: '😊', i18n: 'journal.cat_humeur',   label: 'Humeur',   libre: false },
  { id: 'incident', emoji: '⚠️', i18n: 'journal.cat_incident', label: 'Incident', libre: true  },
];

// Liste COURTE et fermée : une humeur se choisit, elle ne s'invente pas.
export const HUMEURS = [
  { id: 'joyeux',  emoji: '😊', i18n: 'journal.hum_joyeux',  label: 'Joyeux' },
  { id: 'calme',   emoji: '😌', i18n: 'journal.hum_calme',   label: 'Calme' },
  { id: 'neutre',  emoji: '😐', i18n: 'journal.hum_neutre',  label: 'Neutre' },
  { id: 'inquiet', emoji: '😟', i18n: 'journal.hum_inquiet', label: 'Inquiet' },
  { id: 'triste',  emoji: '😢', i18n: 'journal.hum_triste',  label: 'Triste' },
  { id: 'enerve',  emoji: '😠', i18n: 'journal.hum_enerve',  label: 'Énervé' },
  { id: 'fatigue', emoji: '😴', i18n: 'journal.hum_fatigue', label: 'Fatigué' },
];

// Pré-remplissage : les incidents COURANTS. La liste n'a pas vocation à être
// exhaustive (c'est impossible) — au-delà, on écrit son texte.
export const INCIDENTS = [
  { id: 'blessure', emoji: '🤕', i18n: 'journal.inc_blessure', label: 'Blessure' },
  { id: 'maladie',  emoji: '🤒', i18n: 'journal.inc_maladie',  label: 'Maladie' },
  { id: 'dispute',  emoji: '😤', i18n: 'journal.inc_dispute',  label: 'Dispute' },
  { id: 'ecole',    emoji: '🏫', i18n: 'journal.inc_ecole',    label: 'Souci scolaire' },
  { id: 'retard',   emoji: '⏰', i18n: 'journal.inc_retard',   label: 'Retard' },
  { id: 'objet',    emoji: '🧩', i18n: 'journal.inc_objet',    label: 'Objet perdu ou cassé' },
  { id: 'trajet',   emoji: '🚗', i18n: 'journal.inc_trajet',   label: 'Trajet' },
  { id: 'autre',    emoji: '📌', i18n: 'journal.inc_autre',    label: 'Autre' },
];

// ── Accès au vocabulaire ─────────────────────────────────────────
// Nature EFFECTIVE : repli sur 'note' pour tout ce qui n'est pas reconnu
// (document ancien, valeur inattendue) — jamais d'écran vide.
export function natureDe(note) {
  const id = typeof note === 'string' ? note : note?.nature;
  return NATURES_NOTE.some(n => n.id === id) ? id : 'note';
}

export function natureInfo(nature) {
  return NATURES_NOTE.find(n => n.id === natureDe(nature)) || NATURES_NOTE[0];
}

// Le vocabulaire d'une nature. 'note' emprunte celui des événements.
export function vocabulaireDe(nature) {
  switch (natureDe(nature)) {
    case 'humeur':   return HUMEURS;
    case 'incident': return INCIDENTS;
    default:         return CATEGORIES_EVT;
  }
}

// La saisie libre n'est ouverte que là où la nature la déclare.
export function accepteValeurLibre(nature) {
  return !!natureInfo(nature).libre;
}

// ── Libellés ─────────────────────────────────────────────────────
// `t` (useT) optionnel au point d'affichage : sans lui on retombe sur le
// label fr embarqué — même contrat que data/categories.js. L'emoji n'est
// jamais traduit.
const libelleEntree = (e, t) => {
  if (!e) return '';
  const txt = (t && e.i18n) ? t(e.i18n) : (e.label || e.id || '');
  return e.emoji ? `${e.emoji} ${txt}` : txt;
};

export function labelNature(nature, t) {
  return libelleEntree(natureInfo(nature), t);
}

export function emojiNature(nature) {
  return natureInfo(nature).emoji || '📝';
}

// Emoji d'une note : celui de sa VALEUR quand elle en a une (plus parlant
// dans une liste), sinon celui de sa nature.
export function emojiNote(note) {
  const nat = natureDe(note);
  const id = note?.valeurId || '';
  if (!id) return emojiNature(nat);
  if (nat === 'note') return emojiCategorie(id) || emojiNature(nat);
  const e = vocabulaireDe(nat).find(x => x.id === id);
  return e?.emoji || emojiNature(nat);
}

// Libellé de la valeur d'une note : la saisie libre l'emporte (elle n'existe
// que si l'utilisateur l'a écrite), sinon l'entrée du vocabulaire, sinon rien
// — une note peut n'avoir aucune valeur, c'est un cas normal.
export function labelValeur(note, t) {
  const libre = (note?.valeurLibre || '').trim();
  if (libre) return libre;
  const id = note?.valeurId || '';
  if (!id) return '';
  const nat = natureDe(note);
  if (nat === 'note') return labelCategorie(id, t);
  return libelleEntree(vocabulaireDe(nat).find(x => x.id === id), t);
}

// Options prêtes pour <ChoixPuces>. `avecLibre` ajoute la puce « Autre… »
// quand la nature l'autorise — c'est elle qui révèle le champ de saisie.
export function optionsValeur(nature, t, { avecLibre = true } = {}) {
  const nat = natureDe(nature);
  const base = vocabulaireDe(nat).map(e => ({
    id: e.id,
    label: nat === 'note' ? labelCategorie(e.id, t) : libelleEntree(e, t),
  }));
  if (avecLibre && accepteValeurLibre(nat)) {
    base.push({ id: VALEUR_LIBRE, label: t ? `✏️ ${t('journal.valeurLibre')}` : '✏️ Autre…' });
  }
  return base;
}

export function optionsNature(t) {
  return NATURES_NOTE.map(n => ({ id: n.id, label: libelleEntree(n, t) }));
}

// ── Normalisation d'une note (PURE) ──────────────────────────────
// Vit ici et non dans services/journal.js pour rester testable sans Firestore
// — même séparation que occurrences.js / import.js dans My Finance.
// Garantit l'invariant « valeurId XOR valeurLibre » et neutralise tout ce qui
// ne doit pas atteindre la base : l'id sentinelle de la puce « Autre… », une
// saisie libre sur une nature qui ne l'autorise pas, un id inconnu du
// vocabulaire (nature changée après coup, document ancien).
const estIso = (s) => /^\d{4}-\d{2}-\d{2}$/.test(String(s || ''));

export function normaliserNote(n = {}, aujourdhui) {
  const nature = natureDe(n);
  const texte  = String(n.texte || '').trim();
  const auj    = estIso(aujourdhui) ? aujourdhui : new Date().toISOString().slice(0, 10);
  const date   = estIso(n.date) ? n.date : auj;

  const libre = accepteValeurLibre(nature) ? String(n.valeurLibre || '').trim() : '';

  let valeurId = String(n.valeurId || '');
  if (valeurId === VALEUR_LIBRE) valeurId = '';          // sentinelle d'IHM
  if (libre) valeurId = '';                              // la saisie libre l'emporte
  if (valeurId && !vocabulaireDe(nature).some(x => x.id === valeurId)) valeurId = '';

  // L'heure saisie est FACULTATIVE : sans elle, l'affichage et le tri
  // retombent sur `creeLe` (cf. heureTri). Une heure mal formee est ecartee
  // plutot que stockee — on ne range pas en base ce qu'on ne saura pas relire.
  const heure = estHeure(n.heure) ? n.heure : '';

  return { nature, date, heure, texte, valeurId, valeurLibre: libre };
}

// ─── Heure et regroupement par JOUR ───────────────────────────────
// Demande de Cedric (09/08) : voir l'heure de chaque note, une vue JOUR avec
// navigation avant/apres comme le calendrier, et un tri par heure dans la
// journee.
//
// L'heure vient de `creeLe` (horodatage d'ecriture) et n'est PAS un champ
// saisissable : toutes les notes existantes en ont un, donc rien a migrer et
// l'affichage marche retroactivement. Limite assumee : une note ecrite le 20
// POUR le 15 portera l'heure du 20 — c'est l'heure de SAISIE, pas celle du
// fait. Ouvrir une heure saisissable serait un increment a part.
// Heure de TRI, « HH:MM ». Priorite a l'heure SAISIE (Cedric, 09/08 : « ce
// n'est pas toujours sur le moment qu'on remplit »), repli sur l'horodatage
// d'ecriture. Le repli evite toute migration : les notes anterieures gardent
// leur place dans la journee au lieu de remonter en tete.
export const estHeure = (h) => /^([01]\d|2[0-3]):[0-5]\d$/.test(String(h || ''));

export function heureTri(n) {
  if (estHeure(n?.heure)) return n.heure;
  const d = new Date(n?.creeLe || '');
  if (!n?.creeLe || Number.isNaN(d.getTime())) return '';
  return String(d.getHours()).padStart(2, '0') + ':' + String(d.getMinutes()).padStart(2, '0');
}

// Heure AFFICHEE, « 08h56 ».
export function heureNote(n) {
  const h = heureTri(n);
  return h ? h.replace(':', 'h') : '';
}

// Les JOURS presents dans les notes, du plus recent au plus ancien. C'est cette
// liste qui borne la navigation : on ne propose jamais un jour vide.
export function joursDeNotes(notes) {
  const vus = new Set();
  (notes || []).forEach(n => { if (estIso(n?.date)) vus.add(n.date); });
  return Array.from(vus).sort((a, b) => b.localeCompare(a));
}

// Notes d'UN jour, triees par heure CROISSANTE : dans une journee on lit du
// matin vers le soir, alors que la liste globale montre le plus recent d'abord.
export function notesDuJour(notes, jour) {
  return (notes || [])
    .filter(n => n?.date === jour)
    // Du matin vers le soir. `creeLe` DEPARTAGE deux notes de la meme heure —
    // sans ce second critere, leur ordre serait celui, arbitraire, de Firestore.
    .sort((a, b) => heureTri(a).localeCompare(heureTri(b))
      || String(a.creeLe || '').localeCompare(String(b.creeLe || '')));
}

// Jour calendaire voisin : on avance d'un jour, VIDE OU NON (Cedric, 09/08).
// La double fleche, elle, saute au prochain jour qui porte des notes.
export function decalerJour(jour, sens) {
  if (!estIso(jour)) return null;
  const d = new Date(jour + 'T12:00:00Z');      // midi UTC : a l'abri des fuseaux
  d.setUTCDate(d.getUTCDate() + (sens < 0 ? -1 : 1));
  return d.toISOString().slice(0, 10);
}

export const aujourdHui = () => new Date().toISOString().slice(0, 10);

// Jour precedent / suivant AYANT des notes. `sens` : -1 (avant), +1 (apres).
// On saute les jours vides plutot que d'avancer d'un jour calendaire : sinon la
// navigation traverse des ecrans vides sans rien apprendre a personne.
// Renvoie null au bout — l'appelant desactive la fleche.
export function jourVoisin(notes, jour, sens) {
  const jours = joursDeNotes(notes);          // recent -> ancien
  const i = jours.indexOf(jour);
  if (i < 0) return jours[0] || null;
  const cible = sens < 0 ? i + 1 : i - 1;     // « avant » = plus ancien = plus loin
  return jours[cible] || null;
}
